/**
 * Copyright (C) 2021 THL A29 Limited, a Tencent company.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import SyntaxBase from '@/core/SyntaxBase';
import { escapeHTMLSpecialChar as _e, isValidScheme, encodeURIOnce } from '@/utils/sanitize';
import { compileRegExp, isLookbehindSupported, NOT_ALL_WHITE_SPACES_INLINE } from '@/utils/regexp';
import { replaceLookbehind } from '@/utils/lookbehind-replace';
import UrlCache from '@/UrlCache';

export default class Link extends SyntaxBase {
  static HOOK_NAME = 'link';

  constructor({ config, globalConfig }) {
    super({ config });
    this.urlProcessor = globalConfig.urlProcessor;
    this.openNewPage = config.openNewPage; // 是否支持链接新页面打开
  }

  /**
   * 校验link中text的方括号是否符合规则
   * @param {string} rawText
   */
  checkBrackets(rawText) {
    const stack = [];
    const text = `[${rawText}]`;
    // 前方有奇数个\当前字符被转义
    const checkEscape = (place) => text.slice(0, place).match(/\\*$/)[0].length & 1;
    for (let i = text.length - 1; text[i]; i--) {
      if (i === text.length - 1 && checkEscape(i)) break;
      if (text[i] === ']' && !checkEscape(i)) stack.push(']');
      if (text[i] === '[' && !checkEscape(i)) {
        stack.pop();
        if (!stack.length) {
          return {
            isValid: true,
            coreText: text.slice(i + 1, text.length - 1),
            extraLeadingChar: text.slice(0, i),
          };
        }
      }
    }
    return {
      isValid: false, // 方括号匹配不上
      coreText: rawText,
      extraLeadingChar: '',
    };
  }

  /**
   *
   * @param {string} match 匹配的完整字符串
   * @param {string} leadingChar 正则分组一：前置字符
   * @param {string} text 正则分组二：链接文字
   * @param {string|undefined} link 正则分组三：链接URL
   * @param {string|undefined} title 正则分组四：链接title
   * @param {string|undefined} ref 正则分组五：链接引用
   * @param {string|undefined} target 正则分组六：新窗口打开
   * @returns
   */
  toHtml(match, leadingChar, text, link, title, ref, target) {
    const refType = typeof link === 'undefined' ? 'ref' : 'url';
    let attrs = '';
    if (refType === 'ref') {
      // 全局引用，理应在CommentReference中被替换，没有被替换说明没有定义引用项
      return match;
    }

    if (refType === 'url') {
      const { isValid, coreText, extraLeadingChar } = this.checkBrackets(text);
      if (!isValid) return match;
      attrs = title && title.trim() !== '' ? ` title="${_e(title.replace(/["']/g, ''))}"` : '';
      if (target) {
        attrs += ` target="${target.replace(/{target\s*=\s*(.*?)}/, '$1')}"`;
      } else if (this.openNewPage) {
        attrs += ` target="_blank"`;
      }
      let processedURL = link.trim().replace(/~1D/g, '~D'); // 还原替换的$符号
      const processedText = coreText.replace(/~1D/g, '~D'); // 还原替换的$符号
      // text可能是html标签，依赖htmlBlock进行处理
      if (isValidScheme(processedURL)) {
        processedURL = this.urlProcessor(processedURL, 'link');
        processedURL = encodeURIOnce(processedURL);
        return `${leadingChar + extraLeadingChar}<a href="${UrlCache.set(
          processedURL,
        )}" rel="nofollow"${attrs}>${processedText}</a>`;
      }
      return `${leadingChar + extraLeadingChar}<span>${text}</span>`;
    }
    // should never happen
    return match;
  }

  toStdMarkdown(match) {
    return match;
  }

  makeHtml(str) {
    let $str = this.replaceForBrackets(str, (match) => {
      return match.replace(/~D/g, '~1D');
    });
    if (isLookbehindSupported()) {
      $str = this.replaceForBrackets($str, this.toHtml.bind(this));
    } else {
      $str = replaceLookbehind($str, this.RULE.reg, this.toHtml.bind(this), true, 1);
    }
    $str = this.replaceForBrackets($str, (match) => {
      return match.replace(/~1D/g, '~D');
    });
    return $str;
  }

  // 支持link中成对括号的匹配
  replaceForBrackets(str, replacer) {
    return str.replace(this.RULE.reg, (...args) => {
      /**
       * 判断 link 是否存在成对括号
       * 基本逻辑：从左往右找，找到第一个未成对匹配的“（”、“）”，以此位置拦截，重新执行 link replace + remainStr
       */
      const [match, , , link] = args;
      if (link && (link.includes('(') || link.includes(')'))) {
        const stack = [];
        // 处理获取非成对（）信息
        link.split('').some((char, index) => {
          if ([')', '('].includes(char)) {
            const last = stack[stack.length - 1];
            if (!last) {
              stack.push({ char, index });
            } else {
              if (last.char === '(' && char === ')') {
                // 匹配出栈
                stack.pop();
              } else {
                // 压栈
                stack.push({ char, index });
                // 异常结束
                if (last.char === ')') return true;
              }
            }
          }
          return false;
        });
        // 异常处理
        if (stack.length > 0) {
          if (stack[0].char === ')') {
            // 1. 一个) 前置匹配处理
            if (stack.length === 1) {
              const splitMatch = match.split(link);
              const targetStr = splitMatch[0] + link;
              return targetStr.replace(this.RULE.reg, replacer) + this.makeHtml(splitMatch[1]);
            }
            // 2. 前置 )，剩余其他括号处理
            const { index, char } = stack[1];
            const linkIndex = match.indexOf(link);
            const len = linkIndex + index;
            let preStr = match.substring(0, len);
            let remainStr = match.substring(len);
            // 处理 [xxx](xxxx)xxx[xxx](xxxx) 场景，同行多链接配置
            if (char === '(') {
              const headRule = /^.*?(\[[^[\]]+\])$/;
              const result = headRule.exec(preStr);
              if (result) {
                preStr = preStr.substring(0, preStr.length - result[1].length);
                remainStr = result[1] + remainStr;
              }
            }
            return preStr.replace(this.RULE.reg, replacer) + this.makeHtml(remainStr);
          }
          // 3. 多余括号的异常场景，分割独立 make
          const { index } = stack[stack.length - 1];
          const linkIndex = match.indexOf(link);
          const len = linkIndex + index;
          const preStr = match.substring(0, len);
          const remainStr = match.substring(len);
          return this.makeHtml(preStr) + this.makeHtml(remainStr);
        }
      }
      return replacer(...args);
    });
  }

  rule() {
    // (?<protocol>\\w+:)\\/\\/
    const ret = {
      // lookbehind启用分组是为了和不兼容lookbehind的场景共用一个回调
      begin: isLookbehindSupported() ? '((?<!\\\\))' : '(^|[^\\\\])',
      content: [
        '\\[([^\\n]+?)\\]', // ?<text>
        '[ \\t\u00a0]*', // any spaces
        `${
          '(?:' +
          '\\(' +
          /**
           * allow double quotes
           * e.g.
           * [link](") ⭕️ valid
           * [link]("") ⭕️ valid
           * [link](" ") ❌ invalid
           */
          '([^\\s]+)' + // ?<link> url
          '(?:[ \\t\u00a0]((?:".*?")|(?:\'.*?\')))?' + // ?<title> optional
          '\\)' +
          '|' + // or
          '\\[('
        }${NOT_ALL_WHITE_SPACES_INLINE})\\]` + // ?<ref> global ref
          ')',
        '(\\{target\\s*=\\s*(_blank|_parent|_self|_top)\\})?',
      ].join(''),
      end: '',
    };
    // let ret = {begin:'((^|[^\\\\])\\*\\*|([\\s]|^)__)',
    // end:'(\\*\\*([\\s\\S]|$)|__([\\s]|$))', content:'([^\\n]+?)'};
    ret.reg = compileRegExp(ret, 'g');
    return ret;
  }
}
